// Contract-administration letters generated from the contract brief:
//   • Site Handover Letter — the Employer gives right of access to and possession
//     of the Site (FIDIC Sub-Clause 2.1 [Right of Access to the Site]).
//   • Notice to Proceed — notice of the Commencement Date, from which the
//     Contractor must commence the Works (FIDIC Sub-Clause 8.1 [Commencement of Works]).
// Each renders as a standalone one-part document with no title page (noCover).

import type { AssembledContract, Block, CommsEntry, Money, ProjectBrief } from "./types";
import { COMMS_META } from "./types";

const D = "________________";
const or = (v?: string, f = D) => (v && v.trim() ? v : f);
// Number.isFinite, not global isFinite: a JSON round-trip turns NaN amounts into
// null, and isFinite(null) is true — which would crash on .toLocaleString().
const money = (m?: Money) =>
  !m || !Number.isFinite(m.amount) ? D : `${m.currency} ${m.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (n?: number) => (!Number.isFinite(n as number) ? D : `${n}%`);

const letterDate = (b: ProjectBrief) => or(b.letterDate || b.coverDate, "____________");
/** Who issues the letter. The Consultant administers the Contract and issues the
 * contract-administration correspondence; where no Consultant is named on the
 * brief, the Employer issues in its own name. */
const issuer = (b: ProjectBrief) => (b.engineer?.name?.trim() ? b.engineer : b.employer);
const issuerRole = (b: ProjectBrief) => (b.engineer?.name?.trim() ? "Consultant" : "Employer");
/** True when the Consultant (not the Employer) is issuing. */
const hasConsultant = (b: ProjectBrief) => !!b.engineer?.name?.trim();

/** How the letter is signed. These letters are issued under the Consultant's own
 * authority and are not countersigned by the Contractor — a notice is issued, not
 * agreed — so there is exactly one signing party. */
export interface LetterSigning {
  /** Data-URI of the issuer's signature, applied to the signing block. */
  signature?: string;
  /** Date the signature was applied; falls back to the letter date. */
  signedDate?: string;
}

/** The single signing party: us, the issuer. `onBehalfOf` prints above the
 * signing line where we sign for another party's grant (site possession is the
 * Employer's to give under Sub-Clause 2.1 — we only convey it). `party` overrides
 * the issuer for the free-form letter, where the sender is chosen in the UI. */
function signedBy(
  b: ProjectBrief,
  sign?: LetterSigning,
  onBehalfOf?: string,
  party?: { from: { name?: string; representative?: string; representativeTitle?: string }; role: string },
): Block {
  const from = party?.from ?? issuer(b);
  const role = party?.role ?? issuerRole(b);
  return {
    type: "signblock",
    parties: [
      {
        role: `${role} — ${or(from.name, role)}`,
        caption: onBehalfOf,
        name: from.representative,
        title: from.representativeTitle,
        signatureDataUri: sign?.signature,
        signedDate: sign?.signature ? sign.signedDate || letterDate(b) : undefined,
      },
    ],
  };
}

/** Shared letterhead, laid out the way a contract-administration letter actually
 * reads: the issuer's letterhead at the top, then the reference block, the
 * addressee, the salutation, and only then the subject line. The subject IS the
 * letter's title — so no separate document heading is printed above it.
 * `subject` is given bare (no "Subject:" prefix); it is printed in caps. */
function head(b: ProjectBrief, refSuffix: string, subject: string, from: { name?: string; address?: string }): Block[] {
  return [
    // Letterhead — who is issuing this. Level 1 renders with a rule beneath it.
    { type: "heading", text: or(from.name, "The Employer"), level: 1 },
    ...(from.address ? [{ type: "paragraph", text: from.address, align: "left" } as Block] : []),
    { type: "spacer" },
    { type: "paragraph", text: `Ref: ${or(b.contractReference, "____________")}/${refSuffix}`, align: "left" },
    { type: "paragraph", text: `Date: ${letterDate(b)}`, align: "left" },
    { type: "spacer" },
    { type: "paragraph", text: "To:", align: "left" },
    { type: "paragraph", text: `${or(b.contractor.name)}`, align: "left" },
    ...(b.contractor.address ? [{ type: "paragraph", text: b.contractor.address, align: "left" } as Block] : []),
    ...(b.contractor.representative ? [{ type: "paragraph", text: `Attn: ${b.contractor.representative}${b.contractor.representativeTitle ? `, ${b.contractor.representativeTitle}` : ""}`, align: "left" } as Block] : []),
    { type: "spacer" },
    { type: "paragraph", text: "Dear Sirs,", align: "left" },
    { type: "heading", text: subject.toUpperCase(), level: 2 },
    { type: "paragraph", text: `Project: ${or(b.projectName)}${b.projectLocation ? ` — ${b.projectLocation}` : ""}`, align: "left" },
    ...(b.contractReference ? [{ type: "paragraph", text: `Contract: ${b.contractReference}${b.agreementDate ? `, dated ${b.agreementDate}` : ""}`, align: "left" } as Block] : []),
    { type: "spacer" },
  ];
}

/** Recital identifying the Contract both letters refer to. */
function recital(b: ProjectBrief): Block {
  return {
    type: "paragraph",
    text: `We refer to the Contract for ${or(b.worksDescription, "the Works")} at ${or(b.projectLocation)}${b.agreementDate ? `, dated ${b.agreementDate}` : ""}${b.contractReference ? ` (Contract No. ${b.contractReference})` : ""}, made between ${or(b.employer.name)} (the "Employer") and ${or(b.contractor.name)} (the "Contractor").`,
  };
}

// --- Site Handover Letter ---------------------------------------------------
export function assembleHandoverLetter(b: ProjectBrief, ref?: string, sign?: LetterSigning): AssembledContract {
  const extent = or(b.siteHandoverExtent, "the whole of the Site");
  const date = or(b.siteHandoverDate || b.commencementDate, "____________");
  // Possession is the EMPLOYER's to grant under Sub-Clause 2.1. Where we issue as
  // Consultant we convey that grant on the Employer's behalf (stated by "we hereby
  // give you notice…" + the signature caption) — we never give possession in our
  // own right. Where the Employer issues directly, it grants in the first person.
  const grant = hasConsultant(b)
    ? `In accordance with Sub-Clause 2.1 [Right of Access to the Site] of the Conditions of Contract, we hereby give the Contractor notice, on behalf of the Employer, that the Employer gives the Contractor right of access to, and possession of, ${extent}, with effect from ${date} (the "Date of Possession").`
    : `In accordance with Sub-Clause 2.1 [Right of Access to the Site] of the Conditions of Contract, the Employer hereby gives the Contractor right of access to, and possession of, ${extent}, with effect from ${date} (the "Date of Possession").`;

  const blocks: Block[] = [
    ...head(b, ref || "SH-01", "Handover of the Site — Right of Access and Possession (Sub-Clause 2.1)", issuer(b)),
    recital(b),
    { type: "paragraph", text: grant },
    { type: "paragraph", text: "The Contractor is required to take possession on the above date and to note the following:" },
    {
      type: "list",
      items: [
        { prefix: "1.", text: `The extent of the Site handed over by this letter is ${extent}. Any part of the Site not included above will be handed over under separate notice, and this letter does not constitute possession of any such part.` },
        { prefix: "2.", text: "From the Date of Possession, the Contractor shall be responsible for the care of the Site and of the Works in accordance with Sub-Clause 17.2 [Responsibility for Care of the Works], and for the security, safety, protection and control of the Site in accordance with Clause 4 [The Contractor]." },
        { prefix: "3.", text: "The Contractor shall satisfy itself as to the boundaries, levels and existing condition of the Site. A joint condition survey and photographic record shall be made and signed by both Parties, and shall be annexed to this letter." },
        { prefix: "4.", text: "The Contractor shall confine its operations to the Site and shall not encroach upon adjoining property, and shall comply with all applicable permits, laws and regulations in its use of the Site." },
        { prefix: "5.", text: `Existing services, utilities and any known constraints affecting the Site are as previously notified. The Contractor shall notify the ${issuerRole(b)} promptly of any condition encountered which differs from that anticipated.` },
      ],
    },
    { type: "paragraph", text: "This letter is issued for the purpose of Sub-Clause 2.1 only and does not vary the Contract Price, the Time for Completion, or any other term of the Contract. Nothing in this letter relieves the Contractor of any obligation under the Contract." },
    { type: "paragraph", text: "Please acknowledge receipt of this letter and confirm the date on which the Contractor takes possession of the Site." },
    { type: "spacer" },
    { type: "paragraph", text: "Yours faithfully," },
    signedBy(b, sign, hasConsultant(b) ? "For and on behalf of the Employer" : undefined),
  ];

  return {
    title: `${or(b.projectName, "Project")} — Site Handover Letter`,
    standardLabel: "Site Handover Letter — Sub-Clause 2.1 [Right of Access to the Site]",
    masterCover: { title: "Site Handover Letter", subtitle: b.projectName },
    parts: [{ id: "handover", name: "Site Handover Letter", cover: { title: "Site Handover Letter", subtitle: b.projectName }, blocks }],
    notice: "Contract-administration letter generated from the contract brief. Check the extent, dates and party details before issuing. Not legal advice.",
    noCover: true,
  };
}

// --- Register of Communications issued to the Contractor --------------------
/** The ledger of every letter/notice issued, as an exportable register. */
export function assembleCommsRegister(b: ProjectBrief): AssembledContract {
  const log = [...(b.commsLog || [])];
  const blocks: Block[] = [
    { type: "heading", text: "Register of Communications Issued to the Contractor", level: 1 },
    { type: "paragraph", text: `Project: ${or(b.projectName)}${b.projectLocation ? ` — ${b.projectLocation}` : ""}` },
    { type: "paragraph", text: `Contract: ${or(b.contractReference, "____________")}${b.agreementDate ? `, dated ${b.agreementDate}` : ""}` },
    { type: "paragraph", text: `Contractor: ${or(b.contractor.name)}` },
    { type: "paragraph", text: `Register printed: ${letterDate(b)} · ${log.length} communication${log.length === 1 ? "" : "s"} issued.` },
    { type: "spacer" },
  ];

  if (!log.length) {
    blocks.push({ type: "paragraph", text: "No communications have been issued under this Contract to date." });
  } else {
    blocks.push({
      type: "table",
      columns: ["#", "Reference", "Date", "Type", "Subject", "Issued by", "Notes"],
      rows: log.map((e, i) => [
        String(i + 1),
        e.ref || "—",
        e.date || "—",
        COMMS_META[e.kind]?.label || e.kind,
        e.subject || "—",
        e.issuedBy || "—",
        e.note || "—",
      ]),
    });
    blocks.push({ type: "spacer" });
    blocks.push({ type: "paragraph", text: "This register is a record of the communications issued to the Contractor under the Contract. It is maintained for the purpose of contract administration and does not itself vary any term of the Contract." });
  }

  return {
    title: `${or(b.projectName, "Project")} — Register of Communications`,
    standardLabel: "Register of Communications Issued to the Contractor",
    masterCover: { title: "Register of Communications", subtitle: b.projectName },
    parts: [{ id: "comms", name: "Register of Communications", cover: { title: "Register of Communications", subtitle: b.projectName }, blocks }],
    notice: "Register generated from the communications log held against this contract.",
    noCover: true,
  };
}

/** Next sequential reference for a kind, e.g. "NTP-01" → "NTP-02". */
export function nextCommsRef(log: CommsEntry[] | undefined, kind: CommsEntry["kind"]): string {
  const prefix = COMMS_META[kind].prefix;
  const used = (log || [])
    .filter((e) => e.kind === kind)
    .map((e) => { const m = new RegExp(`^${prefix}-(\\d+)$`).exec(e.ref || ""); return m ? parseInt(m[1], 10) : 0; });
  const next = (used.length ? Math.max(...used) : 0) + 1;
  return `${prefix}-${String(next).padStart(2, "0")}`;
}

// --- Request for the Contractor's Programme (Sub-Clause 8.3) ----------------
export function assembleProgrammeRequest(b: ProjectBrief, ref?: string, sign?: LetterSigning): AssembledContract {
  const from = issuer(b);
  const role = issuerRole(b);
  const due = b.programmeDueDate?.trim()
    ? b.programmeDueDate.trim()
    : "within twenty-eight (28) days after receiving the Notice under Sub-Clause 8.1 [Commencement of Works]";

  const blocks: Block[] = [
    ...head(b, ref || "PRG-01", "Request for the Contractor's Detailed Time Programme (Sub-Clause 8.3)", from),
    recital(b),
    { type: "paragraph", text: `In accordance with Sub-Clause 8.3 [Programme] of the Conditions of Contract, the Contractor is required to submit a detailed time programme to the ${role}. The Contractor is hereby requested to submit that programme ${due}.` },
    { type: "paragraph", text: "The programme shall be prepared using recognised planning software, shall show a critical path, and shall include the following:" },
    {
      type: "list",
      items: [
        { prefix: "1.", text: "The order in which the Contractor intends to carry out the Works, including the anticipated timing of each stage of design (if any), the Contractor's Documents, procurement, manufacture, inspection, delivery to Site, construction, erection, testing, commissioning and trial operation." },
        { prefix: "2.", text: `The periods for reviews and for any other submissions, approvals and consents specified in the Contract, and each of the review periods allowed for the ${role}.` },
        { prefix: "3.", text: "The sequence and timing of inspections and tests specified in the Contract." },
        { prefix: "4.", text: "The Commencement Date, the Time for Completion, any Sections, Milestones and key dates, and the date on which the Contractor intends to achieve Completion." },
        { prefix: "5.", text: "The critical path, the logic links between activities, the calendars and constraints used, and the total float on each path — float in the programme belongs to the Project and is available to both Parties." },
        { prefix: "6.", text: "The resources the Contractor intends to deploy: key personnel, labour by trade, Contractor's Equipment, Subcontractors, and the anticipated resource histogram for each main stage." },
        { prefix: "7.", text: "A supporting report describing the methods the Contractor intends to adopt, the main stages of execution of the Works, and any assumptions on which the programme is based." },
        { prefix: "8.", text: "The programme in both editable (native planning file) and PDF format, together with a baseline against which progress will subsequently be measured." },
      ],
    },
    { type: "paragraph", text: `Once accepted, the programme shall be the programme against which progress is measured and reported. The Contractor shall promptly notify the ${role} of any specific probable future events or circumstances which may adversely affect the Works, increase the Contract Price, or delay the execution of the Works.` },
    { type: "paragraph", text: `If at any time the programme ceases to reflect actual progress, or is otherwise inconsistent with the Contractor's obligations, the Contractor shall submit a revised programme to the ${role} in accordance with Sub-Clause 8.3. Submission of a programme does not relieve the Contractor of any obligation under the Contract, and acceptance of a programme shall not constitute an approval of the methods or of any extension of time.` },
    { type: "paragraph", text: "Please acknowledge receipt of this request and confirm the date by which the programme will be submitted." },
    { type: "spacer" },
    { type: "paragraph", text: "Yours faithfully," },
    signedBy(b, sign),
  ];

  return {
    title: `${or(b.projectName, "Project")} — Request for Programme`,
    standardLabel: "Request for the Contractor's Programme — Sub-Clause 8.3 [Programme]",
    masterCover: { title: "Request for Programme", subtitle: b.projectName },
    parts: [{ id: "programme", name: "Request for Programme", cover: { title: "Request for Programme", subtitle: b.projectName }, blocks }],
    notice: "Contract-administration letter generated from the contract brief. Confirm the submission date before issuing. Not legal advice.",
    noCover: true,
  };
}

// --- General letter to the Contractor ---------------------------------------
/** A free-form letter on the same letterhead. `body` is split into paragraphs on
 * blank lines; lines starting with "- " or "1." become a numbered/bulleted list. */
export function assembleCustomLetter(b: ProjectBrief, subject: string, body: string, opts?: { from?: "employer" | "engineer"; reference?: string; sign?: LetterSigning }): AssembledContract {
  const from = opts?.from === "engineer" ? (b.engineer?.name?.trim() ? b.engineer : b.employer) : opts?.from === "employer" ? b.employer : issuer(b);
  const role = opts?.from === "employer" ? "Employer" : opts?.from === "engineer" ? (b.engineer?.name?.trim() ? "Consultant" : "Employer") : issuerRole(b);
  const subj = subject.trim() || "Letter to the Contractor";

  // Split the body into paragraphs. Within a chunk, a trailing run of "- " / "1."
  // lines becomes a list — any lines before it stay a lead-in paragraph, so
  // "The following is required:" followed by 1./2./3. renders as intended.
  const isItem = (l: string) => /^([-•*]|\d+[.)])\s+/.test(l);
  const bodyBlocks: Block[] = [];
  for (const chunk of body.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean)) {
    const lines = chunk.split(/\n/).map((l) => l.trim()).filter(Boolean);
    const firstItem = lines.findIndex(isItem);
    if (firstItem >= 0 && lines.slice(firstItem).every(isItem)) {
      const lead = lines.slice(0, firstItem);
      if (lead.length) bodyBlocks.push({ type: "paragraph", text: lead.join(" ") });
      bodyBlocks.push({
        type: "list",
        items: lines.slice(firstItem).map((l, i) => {
          const m = /^(\d+)[.)]\s+/.exec(l);
          return { prefix: m ? `${m[1]}.` : `${i + 1}.`, text: l.replace(/^([-•*]|\d+[.)])\s+/, "") };
        }),
      });
    } else {
      bodyBlocks.push({ type: "paragraph", text: lines.join(" ") });
    }
  }
  if (!bodyBlocks.length) bodyBlocks.push({ type: "paragraph", text: "____________" });

  const blocks: Block[] = [
    ...head(b, opts?.reference?.trim() || "LTR-01", subj, from),
    recital(b),
    ...bodyBlocks,
    { type: "paragraph", text: "Please acknowledge receipt of this letter." },
    { type: "spacer" },
    { type: "paragraph", text: "Yours faithfully," },
    signedBy(b, opts?.sign, undefined, { from, role }),
  ];

  return {
    title: `${or(b.projectName, "Project")} — ${subj}`,
    standardLabel: `Letter to the Contractor — issued by the ${role}`,
    masterCover: { title: subj, subtitle: b.projectName },
    parts: [{ id: "letter", name: subj, cover: { title: subj, subtitle: b.projectName }, blocks }],
    notice: "Letter generated from the contract brief. Review the wording before issuing. Not legal advice.",
    noCover: true,
  };
}

// --- Notice to Proceed ------------------------------------------------------
export function assembleNoticeToProceed(b: ProjectBrief, ref?: string, sign?: LetterSigning): AssembledContract {
  const from = issuer(b);
  const role = issuerRole(b);
  const commence = or(b.commencementDate, "____________");

  // Conditions precedent / reminders that only apply when configured.
  const reminders: { prefix: string; text: string }[] = [];
  let n = 1;
  if (b.performanceSecurityPercent != null) {
    reminders.push({ prefix: `${n++}.`, text: `Performance Security: the Contractor shall deliver the Performance Security in the amount of ${pct(b.performanceSecurityPercent)} of the Accepted Contract Amount in accordance with Sub-Clause 4.2 [Performance Security], if not already delivered.` });
  }
  reminders.push({ prefix: `${n++}.`, text: "Insurances: the Contractor shall effect and maintain the insurances required under Clause 19 [Insurance] and provide evidence of cover to the Employer." });
  reminders.push({ prefix: `${n++}.`, text: "Programme: the Contractor shall submit a detailed time programme in accordance with Sub-Clause 8.3 [Programme] within 28 days of receiving this Notice." });
  if (b.advancePaymentPercent != null || b.advanceFixedStart || (b.advanceSchedule && b.advanceSchedule.length)) {
    reminders.push({ prefix: `${n++}.`, text: "Advance Payment Guarantee: the Advance Payment shall not become due until the Contractor has delivered the Advance Payment Guarantee required by Sub-Clause 14.2.1." });
  }
  if (b.delayDamagesPerDay) {
    reminders.push({ prefix: `${n++}.`, text: `Delay Damages of ${money(b.delayDamagesPerDay)} per day apply for each day of delay beyond the Time for Completion, in accordance with Sub-Clause 8.8 [Delay Damages]${b.delayDamagesCapPercent != null ? `, capped at ${pct(b.delayDamagesCapPercent)} of the Contract Price` : ""}.` });
  }

  const blocks: Block[] = [
    ...head(b, ref || "NTP-01", "Notice to Proceed — Commencement Date (Sub-Clause 8.1)", from),
    recital(b),
    { type: "paragraph", text: `In accordance with Sub-Clause 8.1 [Commencement of Works] of the Conditions of Contract, notice is hereby given that the Commencement Date is ${commence}.` },
    { type: "paragraph", text: `The Contractor shall commence the execution of the Works as soon as reasonably practicable after the Commencement Date, and shall thereafter proceed with the Works with due expedition and without delay.` },
    { type: "paragraph", text: `The Time for Completion is ${or(b.timeForCompletion)} calculated from the Commencement Date${b.defectsNotification ? `, followed by a Defects Notification Period of ${b.defectsNotification}` : ""}.` },
    ...(reminders.length ? ([{ type: "paragraph", text: "The Contractor's attention is drawn to the following obligations:" }, { type: "list", items: reminders }] as Block[]) : []),
    { type: "paragraph", text: "This Notice is issued under the Contract and does not vary the Contract Price, the Time for Completion, or any other term of the Contract." },
    { type: "paragraph", text: "Please acknowledge receipt of this Notice in writing." },
    { type: "spacer" },
    { type: "paragraph", text: "Yours faithfully," },
    signedBy(b, sign),
  ];

  return {
    title: `${or(b.projectName, "Project")} — Notice to Proceed`,
    standardLabel: "Notice to Proceed — Sub-Clause 8.1 [Commencement of Works]",
    masterCover: { title: "Notice to Proceed", subtitle: b.projectName },
    parts: [{ id: "ntp", name: "Notice to Proceed", cover: { title: "Notice to Proceed", subtitle: b.projectName }, blocks }],
    notice: "Contract-administration notice generated from the contract brief. Confirm the Commencement Date and the conditions precedent before issuing. Not legal advice.",
    noCover: true,
  };
}
